<!--
Displays a link list of data items that can be filtered.
Usage:
  <SearchList name="foo" id="fooId" label="fooName" data="/getfoos" href="showfoo?id={fooId}&name={fooName}&exclude=foo1">
    content
  </SearchList>
  content: any valid html to appear after the search box. Add a <div class="list-group-item" /> to add a default item to the results list.
  name: the name of the data item listed e.g. building
  id: the data item field name of its id e.g. buildingId
  label: the data item field name of its display name e.g. buildingName
  data: the url where data items are retrieved from e.g. /map/buildings
  href: the url to link to when a data item is clicked. Occurrences of {id} and {label}
    will be replaced with the actual value for each data item.
  exclude: optional id of an item to exclude from the list.
@author bmk11u, ab557z
-->
<template>
  <div>
  <Spinner ref="spinner" />
    <div class="input-group m-2 w-auto">
      <div style="border-radius: 16px;border:1px solid #f7f7f7;min-height:36px;background-color:#f7f7f7;margin-left:24px;margin-right:24px;padding-left:16px;width:100%;">
        <font-awesome-icon style="color:var(--uog-dark-blue);" icon="search" size="sm"></font-awesome-icon>
        <input id="search" v-model="filter" type="text" style="padding-left:14px;background-color:#f7f7f7;color:var(--uog-dark-blue);border:0px;margin-top:4px;width:90%;" autocomplete="off" placeholder="Search for a building or room..." />
      </div>
    </div>
    <div class="list-group m-2">
      <slot></slot>
      <router-link v-for="result in results"
        :result="result"
        :key="result[id]"
        :to="parseHref(result)"
        class="buildingItem">
        {{ result[label] }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
//Utilities
import { ref, defineProps, defineEmits, watch, onMounted } from 'vue'
import axios from 'axios'
import {useRoute} from 'vue-router'
import { useMapCommon } from '../composables/MapCommon.js'

const route = useRoute()
const {urlQueryPersist} = useMapCommon()

//Composables
import { useStringUtil }  from '@/composables/StringMethods.js'
const {
  caseInsensitiveContains
} = useStringUtil()

const props = defineProps(
  {
    name: String,
    id: String,
    label: String,
    data: String,
    href: String,
    exclude: String
  }
)

//Data
const items = ref([])
const filter =  ref('')
const results = ref([])
const aliases = ref([])

//Component/element refs
const spinner = ref(null)

onMounted(
  () => {
    spinner.value.spin()
    axios.get('/map/aliases', { withCredentials: false })
      .then(response => {
        aliases.value = response.data
      })
    axios.get(props.data, { withCredentials: false })
      .then(response => {
        items.value = response.data
        // console.log(this.items)
        results.value = []
        // The call to filterItem will initialise the results
        items.value.forEach(filterItem)
        document.getElementById('search').focus()
        emit('dataLoaded', items.value.length)
      })
      .finally(() => {
        spinner.value.stop()
      })
  }
)

function filterItem (item) {
  if (caseInsensitiveContains(item[props.label], filter.value)) {
    if (props.exclude == null || props.exclude !== item[props.id]) {
      results.value.push(item)
    }
  } else {
    for (let i = 0; i < aliases.value.length; i++) {
      if (caseInsensitiveContains(item[props.label], aliases.value[i].buildingName)) {
        if (props.exclude !== item[props.id]) {
          if (caseInsensitiveContains(aliases.value[i].aliasText, filter.value)) {
            results.value.push(item)
            break
          }
        }
      }
    }
  }
}

function parseHref (result) {
  let href = props.href
  href = href.replace('{' + props.id + '}', result[props.id])
  href = href.replace('{' + props.label + '}', result[props.label])
  //Add any url query parameters
  href = urlQueryPersist(href, route)
  return href
}

function length () {
  return items.value.length
}

const emit = defineEmits(['dataLoaded'])

watch (filter, (newVal, oldVal) => {
  results.value = []
  items.value.forEach(filterItem)
})


</script>
